// SPDX-License-Identifier: AGPL-3.0-or-later

// Game performance mode: pauses decorative work (CSS animations, cosmetic canvases) so the app
// stays out of the way while a game runs. Either always on (manual lite mode) or only while the
// window is unfocused. Device-local; not synced.

import Accessibility from '@app/features/accessibility/state/Accessibility';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import Window from '@app/features/window/state/Window';
import {makeAutoObservable, reaction} from 'mobx';
import {useEffect, useState} from 'react';

export const GAME_PERFORMANCE_MODE_CLASS = 'game-performance-mode';
export const GAME_PERFORMANCE_LITE_CLASS = 'game-performance-lite';
const STORAGE_KEY = 'game_performance_mode';

interface GamePerformanceModeState {
	manual: boolean;
	pauseWhenUnfocused: boolean;
}

function getInitialState(): GamePerformanceModeState {
	const raw = AppStorage.getJSON<Partial<GamePerformanceModeState>>(STORAGE_KEY);
	return {
		manual: raw?.manual === true,
		pauseWhenUnfocused: raw?.pauseWhenUnfocused !== false,
	};
}

class GamePerformanceMode {
	manual = false;
	pauseWhenUnfocused = true;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		const initialState = getInitialState();
		this.manual = initialState.manual;
		this.pauseWhenUnfocused = initialState.pauseWhenUnfocused;
	}

	/** True while decorative effects should be paused. */
	get active(): boolean {
		if (this.manual) return true;
		// "Stay fully interactive when unfocused" wins over the automatic pause.
		return this.pauseWhenUnfocused && !Window.focused && !Accessibility.stayInteractiveWhenUnfocused;
	}

	setManual(manual: boolean): void {
		this.manual = manual;
		this.persist();
	}

	toggleManual(): void {
		this.setManual(!this.manual);
	}

	setPauseWhenUnfocused(value: boolean): void {
		this.pauseWhenUnfocused = value;
		this.persist();
	}

	private persist(): void {
		AppStorage.setJSON<GamePerformanceModeState>(STORAGE_KEY, {
			manual: this.manual,
			pauseWhenUnfocused: this.pauseWhenUnfocused,
		});
	}
}

const store = new GamePerformanceMode();

/** For non-observer components (canvas loops). */
export function useGamePerformanceActive(): boolean {
	const [active, setActive] = useState(store.active);
	useEffect(() => reaction(() => store.active, setActive, {fireImmediately: true}), []);
	return active;
}

export default store;
